import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { authService } from '../../services';
import { Button, Input, Card, CardBody } from '../../components/ui';

const COOLDOWN_SECONDS = 60; // 60 seconds cooldown between resends

export default function ResendVerification() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownIntervalRef = useRef(null);

  // Get email from location state (passed from login page) or use empty string
  const emailFromState = location.state?.email || '';

  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      email: emailFromState
    }
  });

  const email = watch('email');

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  // Start cooldown timer
  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
    }
    cooldownIntervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onSubmit = async (data) => {
    if (cooldown > 0 || loading) return; // Prevent spam

    setLoading(true);
    try {
      await authService.resendVerification(data.email);
      setEmailSent(true);
      startCooldown();
      toast.success(t('auth.verificationSent'));
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('errors.serverError');
      toast.error(errorMessage);
      // If rate limited, start cooldown anyway
      if (error.response?.status === 429 || errorMessage.includes('rate limit') || errorMessage.includes('too many')) {
        startCooldown();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('auth.resendVerification')} - ProCTM</title>
      </Helmet>

      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-neutral-dark">
              {emailSent ? t('auth.verificationSent') : t('auth.resendVerification')}
            </h1>
            <p className="mt-2 text-gray-500">
              {emailSent
                ? t('auth.verificationSentCheck')
                : t('auth.resendVerificationDesc')}
            </p>
          </div>

          {emailSent ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-8 h-8 text-success" />
              </div>
              <p className="text-gray-600">
                {t('auth.verificationSentTo')}{' '}
                <span className="font-medium">{email}</span>
              </p>
              <p className="text-sm text-gray-500">
                {t('auth.clickLinkInEmail')}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/verify-email', { state: { email } })}
                className="w-full"
              >
                {t('auth.verifyEmail')}
              </Button>
              <p className="mt-6 text-center text-sm text-gray-500">
                <Link to="/login" className="text-primary font-medium hover:underline">
                  {t('auth.backToLogin')}
                </Link>
              </p>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input
                  label={t('auth.email')}
                  type="email"
                  icon={EnvelopeIcon}
                  placeholder={t('auth.emailPlaceholder')}
                  error={errors.email?.message}
                  {...register('email', {
                    required: t('errors.required'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('errors.invalidEmail')
                    }
                  })}
                />

                <Button
                  type="submit"
                  className="w-full"
                  loading={loading}
                  disabled={cooldown > 0 || loading}
                >
                  {cooldown > 0
                    ? t('auth.resendCooldown', { seconds: cooldown }) || `Please wait ${cooldown}s before resending`
                    : t('auth.resendVerification')}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500">
                {t('auth.rememberPassword')}{' '}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  {t('auth.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
}
